import React, {useMemo} from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    IconButton,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import { Story, StayDetail } from '../../../../services/restserver-openapi';
import StoryCard from '../StoryCard';
import VerticalGroup from './VerticalGroup';
import moment from 'moment';

import { Link as RouterLink } from 'react-router-dom';
import { Link } from '@mui/material';
import {EcoFriendlyIcon} from "../../EcoFriendlyIcon";
import {getReadableCompoundType, getReadableSiteType} from "../../../../utils/typeUtils";

interface StoryAccordionProps {
    stay: StayDetail;
    stories: Story[];
    isExpanded: boolean;
    onToggle: () => void;
    onCreateClick: (stayId: number) => void;
    onEdit: (story: Story) => void;
    onDelete: (story: Story) => void;
    isOwner: boolean;
    canCreateContent: boolean;
    sortDirection: 'ASC' | 'DESC';
    lineColor: string;
}

export default function StoryAccordion({
                                           stay,
                                           stories,
                                           isExpanded,
                                           onToggle,
                                           onCreateClick,
                                           onEdit,
                                           onDelete,
                                           isOwner,
                                           canCreateContent,
                                           sortDirection,
                                           lineColor,
                                       }: StoryAccordionProps) {
    const stayHasStarted = new Date(stay.fromDate!) <= new Date();

    const theme = useTheme();

    const compoundName = stay.stayCompound?.name || 'Unbekannter Ort';
    const compoundId = stay.stayCompound?.compoundId;
    const compoundType = stay.stayCompound ? getReadableCompoundType(stay.stayCompound) : "—";

    const city = stay.stayCompound?.city;
    const country = stay.stayCompound?.country;

    const dateRange = useMemo(() => {
        if (!stay.fromDate || !stay.toDate) return "—";
        return `${moment(stay.fromDate).format("DD.MM.YYYY")} – ${moment(stay.toDate).format("DD.MM.YYYY")}`;
    }, [stay.fromDate, stay.toDate]);

    const siteTitle = useMemo(() => {
        if (!stay.staySite) return "—";
        const base = `${getReadableSiteType(stay.staySite)}: ${stay.staySite.label}`;
        return stay.staySite.sizeId != null ? `${base} (Größe ${stay.staySite.sizeId})` : base;
    }, [stay.staySite]);

    const tooltipText = !canCreateContent
        ? 'Fehlende Berechtigung'
        : stayHasStarted
            ? 'Neue Story erstellen'
            : `Verfügbar ab dem ${new Date(stay.fromDate!).toLocaleDateString('de-DE')}`;

    const sortedStories = [...stories].sort((a, b) => {
        if (a.storyDate === b.storyDate) {
            return sortDirection === 'DESC'
                ? b.storyId! - a.storyId!
                : a.storyId! - b.storyId!;
        }
        return sortDirection === 'DESC'
            ? b.storyDate! - a.storyDate!
            : a.storyDate! - b.storyDate!;
    });

    return (
        <VerticalGroup color={lineColor} thickness={3}>
            <Accordion
                disableGutters
                elevation={0}
                expanded={isExpanded}
                onChange={onToggle}
                sx={{
                    border: `0px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: "text.secondary" }} />}
                    sx={{
                        px: 2,
                        py: 1,
                        "& .MuiAccordionSummary-content": {
                            margin: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                        },
                    }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            sx={{
                                textTransform: "uppercase",
                                fontWeight: 700,
                                fontSize: 12,
                                letterSpacing: 0.6,
                            }}
                            color="text.secondary"
                        >
                            {compoundType}
                        </Typography>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                            {compoundId ? (
                                <Link
                                    component={RouterLink}
                                    to={`/vanlifer/Campsite/${compoundId}`}
                                    underline="hover"
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{
                                        fontWeight: 800,
                                        color: "text.primary",
                                        minWidth: 0,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                    title={compoundName}
                                >
                                    {compoundName}
                                </Link>
                            ) : (
                                <Typography
                                    sx={{
                                        fontWeight: 800,
                                        minWidth: 0,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                    title={compoundName}
                                >
                                    {compoundName}
                                </Typography>
                            )}
                            {stay.stayCompound?.sustainable && <EcoFriendlyIcon sx={{ flex: "0 0 auto" }} />}
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                            {dateRange}
                            {city ? ` • ${city}${country ? `, ${country}` : ""}` : ""}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                            {siteTitle}
                        </Typography>
                    </Box>

                    {/* Right: Add Story */}
                    {isOwner && (
                        <Tooltip title={tooltipText}>
                            <span>
                              <IconButton
                                  size="medium"
                                  onClick={(e) => {
                                      e.stopPropagation();
                                      onCreateClick(stay.stayId!);
                                  }}
                                  disabled={!stayHasStarted || !canCreateContent}
                                  sx={{
                                      mr: 1,
                                      backgroundColor: 'primary.main',
                                      color: 'white',
                                      '&:hover': {
                                          backgroundColor: 'primary.dark',
                                      },
                                  }}
                              >
                                <AddIcon />
                              </IconButton>
                            </span>
                        </Tooltip>
                    )}
                </AccordionSummary>

                <AccordionDetails>
                    <Box mt={1}>
                        {sortedStories.length > 0 ? (
                            sortedStories.map(entry => (
                                <StoryCard
                                    key={entry.storyId}
                                    story={entry}
                                    isOwner={isOwner}
                                    showOwner={false}
                                    onEdit={onEdit}
                                    onDelete={() => onDelete(entry)}
                                />
                            ))
                        ) : (
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                sx={{ fontStyle: 'italic', mt: 1 }}
                            >
                                {stayHasStarted
                                    ? 'Noch keine Story für diesen Aufenthalt.'
                                    : 'Stories können erst ab Beginn des Aufenthalts erstellt werden.'}
                            </Typography>
                        )}
                    </Box>
                </AccordionDetails>
            </Accordion>
        </VerticalGroup>
    );
};